import * as React from "react";
import { createRefetchContainer } from "react-relay";
import withStyles from "@material-ui/core/styles/withStyles";
import { TextField, InputAdornment, CircularProgress } from "@material-ui/core";
import { Check, ReportProblemOutlined } from "@material-ui/icons";

// styles
import { colors } from "@clockwise/web-commons/src/styles/color.styles";

// internal
import { styles } from "./TeamSearchInput.styles";
import { IProps, IState } from "./TeamSearchInput.d";
import { fragments } from "./TeamSearchInput.gql";
import { query } from "./TeamSearchInputRenderer.gql";

const TEAM_NAME_MAX_LENGTH = 240;

class TeamSearchInputComponent extends React.Component<IProps, IState> {
  private onChangeTimer?: ReturnType<typeof setTimeout>;

  constructor(props: IProps) {
    super(props);

    this.state = {
      status: "unset",
      teamName: props.initialTeamName,
      helperText: "",
    };
  }

  /////////////
  // Helpers //
  /////////////

  private onChange = (e: any) => {
    const org = this.props.org;
    const teamName = e.target.value;

    if (this.onChangeTimer) {
      clearTimeout(this.onChangeTimer);
      delete this.onChangeTimer;
    }

    if (this.props.onChange) {
      this.props.onChange(teamName.trim(), false);
    }

    if (teamName && org) {
      this.setState({
        teamName,
        status: "loading",
        helperText: "",
      });

      this.onChangeTimer = setTimeout(() => {
        this.props.relay.refetch(
          {
            orgRelayId: org.id,
            query: teamName.trim(),
          },
          undefined,
          () => this.maybeUpdateState(this.props),
        );
      }, 750);
    } else {
      this.setState({ teamName, status: "unset", helperText: "" });
    }
  };

  private maybeUpdateState = (props: IProps) => {
    const teamQueryResult =
      props.viewer &&
      props.viewer.teamQueryResultErrorable.__typename !== "GraphEntityError" &&
      props.viewer.teamQueryResultErrorable;

    const query = (teamQueryResult && teamQueryResult.query && teamQueryResult.query.trim()) || "";

    const queryMatchesSearch = query === this.state.teamName.trim();

    const teams = teamQueryResult && teamQueryResult.teams;

    if (queryMatchesSearch && teams) {
      const isValid =
        query === props.initialTeamName ||
        (teams.reduce(
          (prev, curr) => prev && curr.teamName.toLowerCase() !== query.toLowerCase().trim(),
          true,
        ) &&
          query.length < TEAM_NAME_MAX_LENGTH);

      if (this.props.onChange) {
        this.props.onChange(query, isValid);
      }

      this.setState({
        status: !query ? "unset" : isValid ? "valid" : "error",
        helperText: isValid
          ? ""
          : query.length >= TEAM_NAME_MAX_LENGTH
          ? "This team name is too long. Please shorten the name."
          : "This team name has been taken. Please choose another.",
      });
    }
  };

  ///////////////
  // Lifecycle //
  ///////////////

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    this.maybeUpdateState(nextProps);
  }

  ///////////////
  // Rendering //
  ///////////////

  public render() {
    const { classes } = this.props;
    const { status, helperText } = this.state;

    return (
      <TextField
        autoFocus
        InputProps={{
          className: "input",
          endAdornment: (
            <InputAdornment position="end">
              {status === "error" ? (
                <ReportProblemOutlined htmlColor={colors.red.standard} />
              ) : status === "valid" ? (
                <Check htmlColor={colors.green.standard} />
              ) : status === "loading" ? (
                <CircularProgress size={24} />
              ) : (
                ""
              )}
            </InputAdornment>
          ),
        }}
        label="Team name"
        InputLabelProps={{
          shrink: true,
        }}
        inputProps={{ maxLength: TEAM_NAME_MAX_LENGTH }}
        helperText={helperText || undefined}
        className={`${classes.textField} ${status}`}
        placeholder="eg. Marketing, Design, Engineering..."
        variant="outlined"
        value={this.state.teamName}
        onChange={this.onChange}
      />
    );
  }
}

/////////////////
// With Styles //
/////////////////

export const TeamSearchInputWithStyles = withStyles(styles)(TeamSearchInputComponent);

///////////
// Relay //
///////////

export const TeamSearchInput = createRefetchContainer(TeamSearchInputWithStyles, fragments, query);
